import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Search } from "lucide-react";
import { useApiSWR } from "@/hooks/useApiSWR";
import PublicPageLayout from "@/pages/PublicPageLayout";
import { PublicTaskTable } from "@/components/public-tasks/PublicTaskTable";
import { PublicTaskBoard } from "@/components/public-tasks/PublicTaskBoard";
import { PublicTaskRoadmap } from "@/components/public-tasks/PublicTaskRoadmap";
import { PublicTaskPager } from "@/components/public-tasks/PublicTaskPager";
import { PublicTaskFilters } from "@/components/public-tasks/PublicTaskFilters";
import {
	ALL_SPRINTS,
	PUBLIC_FILTER_KEYS,
	type PublicFilterKey,
	type PublicFilterValues,
	type PublicView,
} from "@/components/public-tasks/filters";
import {
	getPublicFilterOptions,
	listPublicTasks,
	type PublicTaskQuery,
} from "@/services/public-view.service";

// ponytail: column view loads the most recently updated tasks in one page
// (server cap) and groups them client-side. Per-column paging if this is hit.
const BOARD_LIMIT = 500;

function EmptyState({ icon: IconCmp, title, body }: { icon: typeof Search; title: string; body: string }) {
	return (
		<div className="flex flex-col items-center justify-center py-24 text-center">
			<IconCmp className="mb-3 h-8 w-8 text-border-strong" />
			<p className="mb-1 text-sm font-medium text-foreground">{title}</p>
			<p className="text-xs text-muted">{body}</p>
		</div>
	);
}

export default function PublicTasksPage() {
	const [params, setParams] = useSearchParams();

	const { data: options } = useApiSWR("public-filters", getPublicFilterOptions);

	// No sprint in the URL = the active sprint (or all, when none is active).
	const sprintParam = params.get("sprint");
	const activeSprintId = options?.sprints.find((s) => s.status === "active")?.id;
	const sprint = sprintParam ?? activeSprintId ?? ALL_SPRINTS;
	const sprintReady = sprintParam !== null || !!options;

	const values = {
		...Object.fromEntries(PUBLIC_FILTER_KEYS.map((k) => [k, params.get(k) ?? ""])),
		sprint,
	} as PublicFilterValues;
	const isFiltered = PUBLIC_FILTER_KEYS.some((k) => params.get(k));
	const viewParam = params.get("view");
	const view: PublicView =
		viewParam === "list" || viewParam === "board" ? viewParam : "roadmap";
	const page = Math.max(1, Number(params.get("page")) || 1);

	const query: PublicTaskQuery = {
		project_id: values.project,
		sprint_id: sprint === ALL_SPRINTS ? "" : sprint,
		priority: values.priority,
		assigned_to: values.assignee,
		search: values.q.trim(),
		...(view !== "list"
			? { limit: BOARD_LIMIT }
			: { status: values.status, page }),
	};

	// Hold the task request until the default sprint is known, so the page
	// does not flash every task first.
	const { data, error, isLoading: tasksLoading } = useApiSWR(
		sprintReady ? ["public-tasks", JSON.stringify(query)] : null,
		() => listPublicTasks(query),
		{ keepPreviousData: true, refreshInterval: 60_000 },
	);

	// Filters and view live in the URL so a view can be bookmarked or shared.
	// Any change other than paging resets to page 1.
	const updateParams = useCallback(
		(changes: Record<string, string>) =>
			setParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					for (const [k, v] of Object.entries(changes)) {
						if (v) next.set(k, v);
						else next.delete(k);
					}
					if (!("page" in changes)) next.delete("page");
					return next;
				},
				{ replace: true },
			),
		[setParams],
	);
	const setFilter = useCallback(
		(key: PublicFilterKey, value: string) => updateParams({ [key]: value }),
		[updateParams],
	);
	const clearFilters = useCallback(
		() => updateParams(Object.fromEntries(PUBLIC_FILTER_KEYS.map((k) => [k, ""]))),
		[updateParams],
	);
	// Columns already split by status, so drop that filter when switching.
	const setView = (v: PublicView) =>
		updateParams({ view: v === "roadmap" ? "" : v, status: "" });

	function goToPage(n: number) {
		updateParams({ page: String(n) });
		window.scrollTo({ top: 0 });
	}

	const isLoading = tasksLoading || !sprintReady;
	const tasks = data?.data ?? [];
	const total = data?.total ?? 0;
	const stages = options?.stages ?? [];

	return (
		<PublicPageLayout
			eyebrow="Dev team"
			fullWidth
			title="What we're working on"
			description="A live look at what the team is working on now, what's coming next, and what's done. Updates every minute."
		>
			{/* Open page: keep internal task names out of search results. */}
			<meta name="robots" content="noindex" />
			{error ? (
				<EmptyState
					icon={AlertCircle}
					title="Could not load tasks"
					body="Please refresh the page or try again in a moment."
				/>
			) : (
				<>
					<PublicTaskFilters
						values={values}
						options={options}
						view={view}
						isFiltered={isFiltered}
						onChange={setFilter}
						onClear={clearFilters}
						onViewChange={setView}
					/>

					{!isLoading && total === 0 ? (
						<EmptyState
							icon={Search}
							title="No tasks found"
							body="Try adjusting your filters."
						/>
					) : view === "roadmap" ? (
						<>
							<PublicTaskRoadmap tasks={tasks} stages={stages} loading={isLoading} />
							{total > tasks.length && (
								<p className="mt-4 text-xs text-muted">
									Showing the {tasks.length} most recently updated of {total} tasks.
									Use the filters to narrow it down.
								</p>
							)}
						</>
					) : view === "board" ? (
						<>
							<PublicTaskBoard tasks={tasks} stages={stages} loading={isLoading} />
							{total > tasks.length && (
								<p className="text-xs text-muted">
									Showing the {tasks.length} most recently updated of {total} tasks.
									Use the filters to narrow it down.
								</p>
							)}
						</>
					) : (
						<>
							<PublicTaskTable tasks={tasks} stages={stages} loading={isLoading} />
							<PublicTaskPager
								page={page}
								limit={data?.limit ?? 50}
								total={total}
								loading={isLoading}
								onPageChange={goToPage}
							/>
						</>
					)}
				</>
			)}
		</PublicPageLayout>
	);
}
